import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  InputAdornment,
  Alert,
} from '@mui/material';
import { Iconify } from '../../components/iconify';
import { getPromotionStockByType } from './promotionStockHelpers';

const selectMenuProps = {
  disableScrollLock: true,
  PaperProps: {
    sx: {
      maxHeight: 300,
      overflow: 'auto',
      '& .MuiList-root': {
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
  },
};

const buildMenuProps = (custom = {}) => ({
  ...selectMenuProps,
  ...custom,
  PaperProps: {
    ...(selectMenuProps.PaperProps || {}),
    ...(custom.PaperProps || {}),
    sx: {
      ...(selectMenuProps.PaperProps?.sx || {}),
      ...(custom.PaperProps?.sx || {}),
    },
  },
});

const limitDecimals = (val, max = 2) => {
  if (val === '' || val === null || val === undefined) return '';
  const s = String(val).replace(/[^\d.]/g, '');
  const parts = s.split('.');
  const intPart = parts[0] || '0';
  if (parts.length === 1) return intPart;
  const frac = (parts[1] || '').slice(0, max);
  return frac.length ? `${intPart}.${frac}` : intPart;
};

export default function BuyXDiscount({
  formData,
  handleInputChange,
  handleOpenProductModal,
  handleRemoveProduct,
  handleProductCompulsoryChange,
  handleProductTypeChange,
  handleProductPricingChange,
  buyProductsSearch,
  setBuyProductsSearch,
  errors = {},
  readOnly = false,
  isViewMode = false,
}) {
  const getProductTypeOptions = (split) => {
    if (split === 0) return [{ value: 'Box', label: 'Box' }];
    if (split === 1) return [{ value: 'Piece', label: 'Piece' }];
    if (split === 2)
      return [
        { value: 'Box', label: 'Box' },
        { value: 'Piece', label: 'Piece' },
      ];
    return [{ value: 'Box', label: 'Box' }];
  };

  const getDefaultProductType = (split) => {
    if (split === 0) return 'Box';
    if (split === 1) return 'Piece';
    if (split === 2) return 'Box';
    return 'Box';
  };

  const normalizeTypeValue = (type, split) => {
    if (!type || type === '' || type === 'undefined' || type === 'null' || type === 'Both') {
      return getDefaultProductType(split);
    }
    return type;
  };

  const getSelectedProductType = (product) => {
    return normalizeTypeValue(product.type, product.split);
  };

  const parsePrice = (value) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
  };

  const getPriceByType = (product, mode = 'sales') => {
    const selectedType = getSelectedProductType(product);
    if (selectedType === 'Piece') {
      if (mode === 'offer') {
        return parsePrice(
          product.pieceOfferPrice ??
          product.offerPiecePrice ??
          product.offerPrice ??
          0
        );
      }
      return parsePrice(
        product.pieceSalesPrice ??
        product.piece_sales_price ??
        product.PiecePrice ??
        product.salesPrice ??
        product.price ??
        0
      );
    }
    if (mode === 'offer') {
      return parsePrice(
        product.boxOfferPrice ??
        product.offerBoxPrice ??
        product.offerPrice ??
        0
      );
    }
    return parsePrice(
      product.boxSalesPrice ??
      product.salesPrice ??
      product.price ??
      0
    );
  };

  const formatPrice = (value) => parsePrice(value).toFixed(2);

  const getStockByType = (product) => {
    if (!product) return 0;
    const currentType = product.type || getSelectedProductType(product);
    const selectedType = currentType === 'Piece' || currentType === 'piece' ? 'Piece' :
      currentType === 'Box' || currentType === 'box' ? 'Box' :
        getSelectedProductType(product);
    return getPromotionStockByType(product, selectedType);
  };

  useEffect(() => {
    const productsNeedUpdate = formData.buyProducts.some(product => {
      const normalizedType = normalizeTypeValue(product.type, product.split);
      const hasValidPricing = product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null';
      return product.type !== normalizedType || !hasValidPricing;
    });

    if (productsNeedUpdate) {
      const updatedProducts = formData.buyProducts.map(product => {
        const normalizedType = normalizeTypeValue(product.type, product.split);
        const hasValidPricing = product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null';

        if (product.type !== normalizedType || !hasValidPricing) {
          return {
            ...product,
            type: normalizedType,
            pricingType: hasValidPricing ? product.pricingType : 'sales',
          };
        }
        return product;
      });
      handleInputChange('buyProducts', updatedProducts);
    }
  }, [formData.buyProducts]);

  useEffect(() => {
    if (formData.buyDiscountConfigType === 'value-off' && (!formData.buyXDiscountApplyTo || formData.buyXDiscountApplyTo === 'none')) {
      handleInputChange('buyXDiscountApplyTo', 'bulk');
      if (!formData.buyXDiscountType || formData.buyXDiscountType === 'no-discount') {
        handleInputChange('buyXDiscountType', 'percentage');
      }
    }
  }, [formData.buyDiscountConfigType]);

  const filteredBuyProducts = formData.buyProducts
    .map((product, index) => {
      const hasValidPricing = product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null';
      return {
        ...product,
        type: getSelectedProductType(product),
        pricingType: hasValidPricing ? product.pricingType : 'sales',
        originalIndex: index
      };
    })
    .filter((product) => {
      const searchLower = buyProductsSearch.toLowerCase();
      return (
        product.name.toLowerCase().includes(searchLower) ||
        (product.code && product.code.toLowerCase().includes(searchLower)) ||
        product.id.toString().toLowerCase().includes(searchLower)
      );
    });

  const totalSelectedQuantity = formData.buyProducts.reduce((sum, product) => {
    const quantity = parseInt(product.quantity, 10);
    if (Number.isNaN(quantity) || quantity <= 0) {
      return sum + 1;
    }
    return sum + quantity;
  }, 0);
  const buyQuantityNumber = parseInt(formData.buyQuantity, 10);
  const totalCompulsoryQuantity = formData.buyProducts
    .filter((product) => product.compulsory)
    .reduce((sum, product) => {
      const quantity = parseInt(product.quantity, 10) || 0;
      return sum + quantity;
    }, 0);
  const compulsoryQuantityError =
    buyQuantityNumber > 0 &&
      totalCompulsoryQuantity > buyQuantityNumber
      ? `Compulsory quantity cannot exceed buy quantity (${buyQuantityNumber}). Maximum allowed: ${buyQuantityNumber}`
      : null;
  const isSetValueConfig = !formData.buyDiscountConfigType || formData.buyDiscountConfigType === 'set-value';
  const showSetValueQuantityWarning =
    isSetValueConfig &&
    formData.buyProducts.length > 0 &&
    !Number.isNaN(buyQuantityNumber) &&
    buyQuantityNumber !== totalSelectedQuantity;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, width: '100%' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#003DA5', fontSize: '2rem' }}>
          Buy X at Discount Configuration
        </Typography>

        <Tooltip
          title={
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontSize: '0.6rem' }}>
                • Buy specified items and get discount on their total value
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontSize: '0.6rem' }}>
                • Set a fixed value price or percentage discount
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: '0.6rem' }}>
                • Discount applies to the total of all buy products
              </Typography>
            </Box>
          }
          arrow
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#1e3a5f',
                color: 'white',
                fontSize: '0.465rem',
                maxWidth: 500,
                boxShadow: 2,
                '& .MuiTooltip-arrow': {
                  color: '#1e3a5f',
                },
              },
            },
          }}
        >
          <IconButton size="small" sx={{ color: '#003DA5' }}>
            <Iconify icon="eva:info-fill" />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider color="black" />

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', mt: 0.5 }}>
        <Box sx={{ width: '50%', maxWidth: 800, pt: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, fontSize: '1rem' }}>
            Buy Products
          </Typography>
          <TextField
            fullWidth
            className="input-border text-field-promo"
            label={
              <span>
                Total Buy Quantity<span style={{ color: 'red' }}>*</span>
              </span>
            }
            value={formData.buyQuantity}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                handleInputChange('buyQuantity', value);
              }
            }}
            onKeyDown={(e) => {
              if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onWheel={(e) => e.target.blur()}
            placeholder="Enter total buy quantity"
            type="number"
            inputProps={{ min: 1, max: 9999 }}
            InputProps={{
              readOnly: readOnly,
            }}
            error={!!errors.buyQuantity || !!compulsoryQuantityError}
            helperText={errors.buyQuantity || compulsoryQuantityError}
            sx={{
              mb: 2,
              '& input[type=number]': { MozAppearance: 'textfield' },
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              }

            }}
          />

          {compulsoryQuantityError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 1,
                '& .MuiAlert-icon': {
                  color: '#FF5630'
                }
              }}
            >
              {compulsoryQuantityError}
            </Alert>
          )}

          {/* {showSetValueQuantityWarning && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: 1,
              '& .MuiAlert-icon': {
                color: '#FF9800'
              }
            }}
          >
            Total buy quantity {buyQuantityNumber} must match selected quantity {totalSelectedQuantity} for set value pricing.
          </Alert>
        )} */}

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Discount Configuration
            </Typography>
            <RadioGroup
              value={formData.buyDiscountConfigType || 'set-value'}
              onChange={(e) => handleInputChange('buyDiscountConfigType', e.target.value)}
              row
              sx={{ mb: 2 }}
            >
              <FormControlLabel value="set-value" control={<Radio className="checkBox" disabled={readOnly} />} label="Set Value" />
              <FormControlLabel value="value-off" control={<Radio className="checkBox" disabled={readOnly} />} label="Value Off" />
            </RadioGroup>

            {(!formData.buyDiscountConfigType ||
              formData.buyDiscountConfigType === 'set-value') && (
                <>
                  <TextField
                    className="input-border"
                    fullWidth
                    size="small"
                    label="Set Value Amount (£)"
                    placeholder="11.99"
                    value={formData.buySetValue || ''}
                    onChange={(e) => handleInputChange('buySetValue', e.target.value)}
                    onKeyDown={(e) => {
                      if (['e', 'E', '+', '-'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    type="number"
                    inputProps={{ min: 0.01, step: 0.01 }}
                    InputProps={{ readOnly: readOnly }}
                    error={!!errors.buySetValue}
                    helperText={errors.buySetValue}
                  />
                  {errors.buySetValueWarning && (
                    <Alert
                      severity="warning"
                      sx={{
                        mt: 1,
                        borderRadius: 1,
                        '& .MuiAlert-icon': {
                          color: '#FF9800'
                        }
                      }}
                    >
                      {errors.buySetValueWarning}
                    </Alert>
                  )}
                  {!errors.buySetValue && !errors.buySetValueWarning && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                      Total price for all products combined
                    </Typography>
                  )}
                </>
              )}

            {formData.buyDiscountConfigType === 'value-off' && (
              <Box
                sx={{
                  bgcolor: '#fffbea',
                  border: 1,
                  borderColor: '#ffd700',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Item Discount on Buy Products (Optional)
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel>Apply Discount To</InputLabel>
                  <Select
                    className="input-border"
                    value={formData.buyXDiscountApplyTo === 'bulk' || formData.buyXDiscountApplyTo === 'per-item' ? formData.buyXDiscountApplyTo : 'bulk'}
                    label="Apply Discount To"
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange('buyXDiscountApplyTo', value);
                      if (value === 'bulk' && (!formData.buyXDiscountType || formData.buyXDiscountType === 'no-discount')) {
                        handleInputChange('buyXDiscountType', 'percentage');
                      }
                    }}
                    MenuProps={buildMenuProps()}
                    disabled={readOnly}
                  >
                    <MenuItem value="bulk">Bulk (All Buy Products Total)</MenuItem>
                    <MenuItem value="per-item">Per Item (Each buy products)</MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#d32f2f' }}>

                  Per Item: Configure discount for each buy product individually
                  <br />
                  Bulk: Apply single discount to total of all buy products
                </Typography>

                {formData.buyXDiscountApplyTo === 'bulk' && (
                  <>
                    <FormControl fullWidth size="small" sx={{ mb: 1 }} error={!!errors.buyXDiscountType}>
                      <InputLabel>Discount Type</InputLabel>
                      <Select
                        className="input-border"
                        value={formData.buyXDiscountType || 'percentage'}
                        label="Discount Type"
                        onChange={(e) => handleInputChange('buyXDiscountType', e.target.value)}
                        MenuProps={buildMenuProps()}
                        disabled={readOnly}
                      >

                        <MenuItem value="percentage">Percentage (%)</MenuItem>
                        <MenuItem value="fixed">Fixed Amount</MenuItem>
                      </Select>
                      {errors.buyXDiscountType && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          {errors.buyXDiscountType}
                        </Typography>
                      )}
                    </FormControl>

                    <TextField
                      className="input-border text-field"
                      fullWidth
                      size="small"
                      label="Discount Value"
                      placeholder="Enter discount value"
                      value={formData.buyXDiscountValue || ''}
                      onChange={(e) => {
                        let raw = e.target.value;
                        const type = (formData.buyXDiscountType || 'percentage');
                        if (type === 'percentage') {
                          const num = parseFloat(raw);
                          if (!Number.isNaN(num) && num > 100) {
                            handleInputChange('buyXDiscountValue', '100');
                            return;
                          }
                          if (raw === '' || raw === '.') {
                            handleInputChange('buyXDiscountValue', raw);
                            return;
                          }
                        }
                        const sanitized = limitDecimals(raw, 2);
                        if (type === 'percentage') {
                          const num = parseFloat(sanitized);
                          if (!Number.isNaN(num) && num > 100) {
                            handleInputChange('buyXDiscountValue', '100');
                            return;
                          }
                        }
                        handleInputChange('buyXDiscountValue', sanitized);
                      }}
                      onBlur={(e) => {
                        const type = (formData.buyXDiscountType || 'percentage');
                        if (type === 'percentage') {
                          const num = parseFloat(e.target.value);
                          if (!Number.isNaN(num) && num > 100) {
                            handleInputChange('buyXDiscountValue', '100');
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (['e', 'E', '+', '-'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                      type="number"
                      inputProps={{
                        min: 0,
                        max: (formData.buyXDiscountType || 'percentage') === 'percentage' ? 100 : 999999,
                        step: (formData.buyXDiscountType || 'percentage') === 'percentage' ? 1 : 0.01,
                      }}
                      InputProps={{ readOnly: readOnly }}
                      error={!!errors.buyXDiscountValue}
                      helperText={errors.buyXDiscountValue || ((formData.buyXDiscountType || 'percentage') === 'percentage' ? 'Maximum 100%' : '')}
                    />
                  </>
                )}
              </Box>
            )}
          </Box>

          {errors.compulsoryProducts && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                borderRadius: 1,
                '& .MuiAlert-icon': {
                  color: '#FF9800'
                }
              }}
            >
              {errors.compulsoryProducts}
            </Alert>
          )}

          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                Selected Products ({formData.buyProducts.length})
              </Typography>
              {errors.buyProducts && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {errors.buyProducts}
                </Typography>
              )}
            </Box>
            {!readOnly && (
              <Button
                variant="outlined"
                className="button-variant-outlined"

                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={() => handleOpenProductModal('buy')}
                disabled={formData.buyProducts.length >= 200}
                disableRipple
              >
                Add More Products
              </Button>
            )}
          </Box>
          {formData.buyProducts.length > 0 && (
            <Box sx={{ mt: 1, maxHeight: 400, overflowY: 'auto', pr: 1 }}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  pb: 1,
                  mb: 1,

                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <TextField
                  className="allow-interaction input-border"
                  fullWidth
                  placeholder="Search by product name or product code"
                  value={buyProductsSearch}
                  onChange={(e) => setBuyProductsSearch(e.target.value)}
                  InputProps={{
                    sx: { height: 40 },
                    className: 'allow-interaction',
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Stack spacing={2}>
                {filteredBuyProducts.map((product) => (
                  <Card
                    key={`${product.id}-${product.originalIndex}`}
                    sx={{ border: 1, borderColor: 'rgba(59, 59, 59, 0.54)', boxShadow: 1 }}
                  >
                    <CardContent sx={{ px: 2, py: 1, '&:last-child': { pb: 1 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.80rem', color: '#003DA5' }}
                          >
                            {product.name}
                          </Typography>
                          <Typography
                            key={`stock-${product.id}-${product.originalIndex}-${getSelectedProductType(product)}`}
                            variant="body2"
                            sx={{ fontSize: '0.75rem', mb: 0.5 }}
                          >
                            Code: {product.code} | Stock: {getStockByType(product)}
                          </Typography>
                        </Box>
                        {!readOnly && (
                          <IconButton
                            onClick={() =>
                              handleRemoveProduct(
                                `${product.id}-${product.originalIndex}`,
                                'buy'
                              )
                            }
                            size="small"
                            color="error"
                          >
                            <Iconify icon="solar:close-circle-outline" />
                          </IconButton>
                        )}
                      </Box>

                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={product.compulsory ? 3 : 4}>
                          <FormControl fullWidth size="small">
                            <InputLabel className="commonColor">Product Type</InputLabel>
                            <Select
                              className="input-border"
                              value={getSelectedProductType(product)}
                              label="Product Type"
                              onChange={(e) =>
                                handleProductTypeChange(
                                  `${product.id}-${product.originalIndex}`,
                                  'buy',
                                  e.target.value
                                )
                              }
                              MenuProps={buildMenuProps()}
                              disabled={readOnly}
                            >
                              {getProductTypeOptions(product.split || 2).map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={product.compulsory ? 4 : 4}>
                          <FormControl fullWidth size="small">
                            <InputLabel className="commonColor">Pricing</InputLabel>
                            <Select
                              className="input-border"
                              value={(product.pricingType && product.pricingType !== 'undefined' && product.pricingType !== 'null') ? product.pricingType : 'sales'}
                              label="Pricing"
                              onChange={(e) =>
                                handleProductPricingChange(
                                  `${product.id}-${product.originalIndex}`,
                                  'buy',
                                  e.target.value
                                )
                              }
                              error={!!errors[`buyProduct_${product.originalIndex}_pricing`] || !!errors[`buyProduct_${product.originalIndex}_offerPrice`]}
                              disabled={readOnly}
                              renderValue={(selected) =>
                                selected === 'offer'
                                  ? `Offer`
                                  : `Standard`
                              }
                              MenuProps={buildMenuProps()}
                              sx={{
                                '& .MuiSelect-select': {
                                  whiteSpace: 'normal',
                                  display: 'block',
                                  lineHeight: 1.4,
                                },
                              }}
                            >
                              <MenuItem value="sales" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                Standard
                              </MenuItem>
                              <MenuItem value="offer" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                Offer
                              </MenuItem>
                            </Select>
                            {(errors[`buyProduct_${product.originalIndex}_pricing`] || errors[`buyProduct_${product.originalIndex}_offerPrice`]) && (
                              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                {errors[`buyProduct_${product.originalIndex}_pricing`] || errors[`buyProduct_${product.originalIndex}_offerPrice`]}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={product.compulsory ? 3 : 4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={product.compulsory}
                                onChange={
                                  readOnly
                                    ? undefined
                                    : (e) =>
                                      handleProductCompulsoryChange(
                                        `${product.id}-${product.originalIndex}`,
                                        'buy',
                                        e.target.checked
                                      )
                                }
                                disabled={readOnly}
                                sx={{
                                  color: '#F8991D',
                                  '&.Mui-checked': { color: '#F8991D' }
                                }}
                              />
                            }
                            label="Compulsory"
                          />
                        </Grid>
                        {product.compulsory && (() => {
                          const buyQuantityNum = parseInt(formData.buyQuantity, 10) || 0;
                          const otherCompulsoryTotal = formData.buyProducts
                            .filter((p, idx) => p.compulsory && idx !== product.originalIndex)
                            .reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 0), 0);
                          const maxAllowedQuantity = buyQuantityNum - otherCompulsoryTotal;

                          return (
                            <Grid item xs={12} sm={2}>
                              <TextField
                                fullWidth
                                size="small"
                                className="input-border"
                                label={
                                  <span>
                                    Qty <span style={{ color: 'red' }}>*</span>
                                  </span>
                                }
                                value={product.quantity}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= 9999)) {
                                    const numValue = value === '' ? 0 : (parseInt(value) || 0);
                                    const buyQty = parseInt(formData.buyQuantity, 10) || 0;
                                    const otherCompulsoryTotal = formData.buyProducts
                                      .filter((p, idx) => p.compulsory && idx !== product.originalIndex)
                                      .reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 0), 0);

                                    if (buyQty > 0 && numValue > 0 && (numValue + otherCompulsoryTotal) > buyQty) {
                                      return;
                                    }

                                    const newBuyProducts = [...formData.buyProducts];
                                    newBuyProducts[product.originalIndex] = {
                                      ...newBuyProducts[product.originalIndex],
                                      quantity: value === '' ? '' : numValue,
                                    };
                                    handleInputChange('buyProducts', newBuyProducts);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                type="number"
                                inputProps={{
                                  min: 1,
                                  max: buyQuantityNum > 0 ? Math.min(9999, maxAllowedQuantity) : 9999
                                }}
                                InputProps={{ readOnly: readOnly }}
                                onWheel={(e) => e.target.blur()}
                                error={!!errors[`buyProduct_${product.originalIndex}_quantity`]}
                                helperText={errors[`buyProduct_${product.originalIndex}_quantity`]}
                                sx={{
                                  '& input[type=number]': { MozAppearance: 'textfield' },
                                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                }}
                              />
                            </Grid>
                          );
                        })()}
                      </Grid>

                      {formData.buyDiscountConfigType === 'value-off' &&
                        formData.buyXDiscountApplyTo === 'per-item' && (
                          <Box
                            sx={{
                              mt: 2,
                              bgcolor: '#fffbea',
                              border: 1,
                              borderColor: '#ffd700',
                              px: 1,
                              pb: 1,
                              borderRadius: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1,
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Item Discount
                              </Typography>
                              <Checkbox
                                checked={product.buyXItemDiscountType !== 'no-discount' && !!(product.buyXItemDiscountValue && product.buyXItemDiscountValue !== '' && parseFloat(product.buyXItemDiscountValue) > 0)}
                                disabled={readOnly}
                                size="small"
                                className="checkBox"
                              />
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Discount Type</InputLabel>
                                  <Select
                                    value={product.buyXItemDiscountType || 'no-discount'}
                                    label="Discount Type"
                                    onChange={(e) => {
                                      const newBuyProducts = [...formData.buyProducts];
                                      newBuyProducts[product.originalIndex] = {
                                        ...newBuyProducts[product.originalIndex],
                                        buyXItemDiscountType: e.target.value,
                                      };
                                      handleInputChange('buyProducts', newBuyProducts);
                                    }}
                                    MenuProps={buildMenuProps()}
                                    disabled={readOnly}
                                  >
                                    <MenuItem value="no-discount">No Discount</MenuItem>
                                    <MenuItem value="percentage">Percentage (%)</MenuItem>
                                    <MenuItem value="fixed">Fixed Amount (£)</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={6}>
                                {product.buyXItemDiscountType !== 'no-discount' && (
                                  <TextField
                                    className="form-input-border"
                                    fullWidth
                                    size="small"
                                    label="Discount Value"
                                    placeholder="e.g., 10"
                                    value={product.buyXItemDiscountValue || ''}
                                    onChange={(e) => {
                                      let raw = e.target.value;
                                      if (product.buyXItemDiscountType === 'percentage') {
                                        const num = parseFloat(raw);
                                        if (!Number.isNaN(num) && num > 100) {
                                          const newBuyProducts = [...formData.buyProducts];
                                          newBuyProducts[product.originalIndex] = {
                                            ...newBuyProducts[product.originalIndex],
                                            buyXItemDiscountValue: '100',
                                          };
                                          handleInputChange('buyProducts', newBuyProducts);
                                          return;
                                        }
                                        if (raw === '' || raw === '.') {
                                          const newBuyProducts = [...formData.buyProducts];
                                          newBuyProducts[product.originalIndex] = {
                                            ...newBuyProducts[product.originalIndex],
                                            buyXItemDiscountValue: raw,
                                          };
                                          handleInputChange('buyProducts', newBuyProducts);
                                          return;
                                        }
                                      }
                                      const sanitized = limitDecimals(raw, 2);
                                      if (product.buyXItemDiscountType === 'percentage') {
                                        const num = parseFloat(sanitized);
                                        if (!Number.isNaN(num) && num > 100) {
                                          const newBuyProducts = [...formData.buyProducts];
                                          newBuyProducts[product.originalIndex] = {
                                            ...newBuyProducts[product.originalIndex],
                                            buyXItemDiscountValue: '100',
                                          };
                                          handleInputChange('buyProducts', newBuyProducts);
                                          return;
                                        }
                                      }
                                      const newBuyProducts = [...formData.buyProducts];
                                      newBuyProducts[product.originalIndex] = {
                                        ...newBuyProducts[product.originalIndex],
                                        buyXItemDiscountValue: sanitized,
                                      };
                                      handleInputChange('buyProducts', newBuyProducts);
                                    }}
                                    onKeyDown={(e) => {
                                      if (['e', 'E', '+', '-'].includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onWheel={(e) => e.target.blur()}
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                      max: product.buyXItemDiscountType === 'percentage' ? 100 : 999999,
                                      step: product.buyXItemDiscountType === 'percentage' ? 1 : 0.01,
                                    }}
                                    onBlur={(e) => {
                                      if (product.buyXItemDiscountType === 'percentage') {
                                        const num = parseFloat(e.target.value);
                                        if (!Number.isNaN(num) && num > 100) {
                                          const newBuyProducts = [...formData.buyProducts];
                                          newBuyProducts[product.originalIndex] = {
                                            ...newBuyProducts[product.originalIndex],
                                            buyXItemDiscountValue: '100',
                                          };
                                          handleInputChange('buyProducts', newBuyProducts);
                                        }
                                      }
                                    }}
                                    InputProps={{ readOnly: readOnly }}
                                    error={!!errors[`buyProduct_${product.originalIndex}_discountValue`]}
                                    helperText={errors[`buyProduct_${product.originalIndex}_discountValue`] || (product.buyXItemDiscountType === 'percentage' ? 'Maximum 100%' : '')}
                                  />
                                )}
                              </Grid>
                            </Grid>
                            {errors[`buyProduct_${product.originalIndex}_discountValue`] && (
                              <Alert severity="error" sx={{ mt: 1, borderRadius: 1 }}>
                                {errors[`buyProduct_${product.originalIndex}_discountValue`]}
                              </Alert>
                            )}
                          </Box>
                        )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
